import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// Org Chart — display-only, data-driven from ntrg_staff.reports_to (editing is via the Employee CRUD).
// Hand-built SVG: each box's (x,y) is COMPUTED from the data (x by leaf order for tight spacing,
// y by depth); connectors are drawn box-to-box by computed coords, so a moved box still connects
// cleanly to its top edge. No library. Cycle-guarded. Retired excluded.

// The ONLY hardcoded bit: a presentation-offset map (visual exceptions, NOT structure/data).
// Keyed by name → extra dx/dy in px. reports_to is never changed by this.
const PRESENTATION_OFFSET = { 'Philip Vivier': { dx: 0, dy: 46 } };

const BOX_W = 152, BOX_H = 54;   // box size
const X_STEP = 160;              // horizontal distance between adjacent leaves (tight)
const Y_STEP = 112;              // vertical distance between depth levels
const PAD = 24;

export default function OrgChart({ darkMode }) {
  const isDark = darkMode;
  const c = isDark
    ? { card: '#2F3854', border: '#434C68', text: '#e8eaf2', muted: '#a8b0cc', line: '#4a5578' }
    : { card: '#ffffff', border: '#d7dbe6', text: '#1c1c1e', muted: '#6a7080', line: '#c2c8d6' };

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('ntrg_staff').select('id, name, title, reports_to, active').order('name').then(({ data }) => {
      setStaff((data || []).filter(p => p.active));  // exclude retired
      setLoading(false);
    });
  }, []);

  // ── Build hierarchy (manager id → direct reports). Manager missing/retired ⇒ treat as root. ──
  const ids = new Set(staff.map(p => p.id));
  const childrenByMgr = {};
  staff.forEach(p => {
    const key = (p.reports_to && ids.has(p.reports_to)) ? p.reports_to : '__root__';
    (childrenByMgr[key] = childrenByMgr[key] || []).push(p);
  });
  const roots = childrenByMgr['__root__'] || [];

  // ── Layout: leaf-order x, depth y, parents centered over their children. Cycle-guarded. ──
  const pos = {};               // id → { ...person, x, y }
  let leafX = 0;
  const place = (node, depth, visited) => {
    if (visited.has(node.id)) return;            // cycle guard
    const v = new Set(visited); v.add(node.id);
    const kids = childrenByMgr[node.id] || [];
    kids.forEach(k => place(k, depth + 1, v));
    const placed = kids.map(k => pos[k.id]).filter(Boolean);
    const x = placed.length
      ? (placed[0].x + placed[placed.length - 1].x) / 2   // center over children
      : (leafX++ * X_STEP + BOX_W / 2);                    // leaf gets the next slot
    const off = PRESENTATION_OFFSET[node.name] || { dx: 0, dy: 0 };
    pos[node.id] = { ...node, x: x + off.dx, y: depth * Y_STEP + off.dy };
  };
  roots.forEach(r => place(r, 0, new Set()));

  const nodes = Object.values(pos);

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;
  if (nodes.length === 0) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>No staff to chart.</div>;

  // ── Canvas extents (for viewBox → one uniform scale-to-fit, no horizontal scroll) ──
  const left = Math.min(...nodes.map(n => n.x - BOX_W / 2));
  const right = Math.max(...nodes.map(n => n.x + BOX_W / 2));
  const bottom = Math.max(...nodes.map(n => n.y)) + BOX_H;
  const totalW = (right - left) + PAD * 2;
  const totalH = bottom + PAD * 2;

  return (
    <div>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>Organizational Chart</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '18px' }}>Reporting structure (maintained via Employee Information).</div>
      <svg viewBox={`${left - PAD} ${-PAD} ${totalW} ${totalH}`} style={{ width: '100%', maxWidth: `${totalW}px`, height: 'auto', display: 'block' }}>
        {/* connectors — clean bus/elbow PER PARENT so every child ties to its manager, never a sibling:
            (1) parent drops from its bottom-center to a shared horizontal bus,
            (2) the bus spans all children (extended to the parent's x when offset, e.g. Philip),
            (3) each child rises from its own top-center to that bus.
            The bus sits in the gap between the parent and children rows — clear of every box. */}
        {nodes.filter(n => (childrenByMgr[n.id] || []).length > 0).map(parent => {
          const kids = (childrenByMgr[parent.id] || []).map(k => pos[k.id]).filter(Boolean);
          if (kids.length === 0) return null;
          const parentBottom = parent.y + BOX_H;
          const minChildTop = Math.min(...kids.map(k => k.y));
          const busY = (parentBottom + minChildTop) / 2;   // midpoint of the gap → never over a box
          const xs = [...kids.map(k => k.x), parent.x];
          const busLeft = Math.min(...xs), busRight = Math.max(...xs);
          return (
            <g key={`c-${parent.id}`} stroke={c.line} strokeWidth="1.5" fill="none">
              <path d={`M ${parent.x} ${parentBottom} V ${busY}`} />
              <path d={`M ${busLeft} ${busY} H ${busRight}`} />
              {kids.map(k => <path key={k.id} d={`M ${k.x} ${busY} V ${k.y}`} />)}
            </g>
          );
        })}
        {/* boxes (foreignObject → HTML for clean name/title styling) */}
        {nodes.map(n => (
          <foreignObject key={`b-${n.id}`} x={n.x - BOX_W / 2} y={n.y} width={BOX_W} height={BOX_H}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '5px 9px', borderRadius: '8px', border: `1px solid ${c.border}`, background: c.card, boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', fontFamily: "'Public Sans',system-ui,sans-serif", overflow: 'hidden' }} title={n.title || n.name}>
              <div style={{ fontWeight: '700', fontSize: '12px', color: c.text, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{n.name}</div>
              {n.title && <div style={{ fontSize: '10px', color: c.muted, lineHeight: 1.2, marginTop: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.title}</div>}
            </div>
          </foreignObject>
        ))}
      </svg>
    </div>
  );
}
